import { FC, Fragment, ReactElement, ReactNode, useEffect } from "react";
import { observer } from "mobx-react";
import { TMessage } from "../i18n/tt";
import { Box, Card, Grid, GridItem, VBox } from "../components-v2/Box";
import { Logo } from "../components-v2/Logo";
import { portfolioUrl } from "../utils/navigation";
import { formatOrgNum, removeTitleAndTD } from "../utils/other";
import { DocumentPreview } from "../components-v2/DocumentPreview";
import { UploadedAttachment } from "../fileUpload/fileUploadDomain";
import {
  BackButton,
  Button,
  ButtonProps,
  IconButton,
  LinkButton,
  ToggleButton,
} from "../components-v2/Button";
import { LocalizedTextForDocument, RenderedDecisionRow, SignerType } from "./common";
import { Alert } from "../components-v2/Alert";
import { downloadFile } from "../api";
import { DropdownMenu } from "../components-v2/Dropdown";
import { isMobileView } from "../utils/screen";
import { formatDateTime, formatTime } from "../i18n/dateFormat";
import { isNotBlank } from "../utils/strings";
import parse from "html-react-parser";
import { Language } from "../i18n/languages";
import { Tag, TagProps } from "../components-v2/Tag";
import { Icon } from "../components-v2/Icon";
import { compact } from "lodash";
import { AttachmentsPanel } from "../components-v2/AttachmentsPanel";
import { Markdown } from "../text/Markdown";
import { DocumentType } from "./documents";
import { DCCallback, useLanguage, useRootStore, useTranslation } from "../stores/RootStoreContext";
import { Dialog } from "../components-v2/Dialog";
import { IconName } from "../components-v2/iconMapping";
import { MCheckboxGroup } from "../components-v2/Checkbox";
import Form from "../components-v2/Form";
import { MTextInput } from "../components-v2/TextInput";
import {
  CommonSigningProperties,
  CompaniesForSigning,
  DocumentsForSigning,
  QuickAddSignerItems,
  RadioFieldModel,
  SendSealedCopyToOtherModel,
  SignerModel,
  SignersForm,
  StringFieldModel,
} from "./commonForms";
import { Table } from "../components-v2/Table";
import { when } from "../utils/functions";
import { MRadioButtonGroup } from "../components-v2/RadioButton";
import { MSelect, Select } from "../components-v2/Select";
import { preventDefault } from "../utils/events";
import { InfoButton } from "../components-v2/InfoButton";
import { FullScreenLayout } from "../components-v2/FullScreenLayout";
import { HiddenForMobile } from "../components-v2/Mobile";
import { FilePreview, FilePreviewProps } from "../components-v2/FilePreview";
import { LocalizedTextEditor } from "../text/LocalizedTextEditor";
import { copyToClipboard } from "../events/eventsDomain";
import { ConfigureSharepointDestination } from "../sharepoint/sharepointComponents";
import { TMessageKey } from "../i18n/types.gen";
import { formatUserName } from "../types/UserInfo";
import { useSize } from "../components-v2/useSize";
import { getActionLabel } from "../archive/commonArchiveDomain";

export const LogoWithCompanyName: FC<{
  companyName?: string;
  orgNo?: string;
  backUrl?: string;
}> = observer(({ companyName, orgNo, backUrl }) => {
  const history = useRootStore().history;
  return (
    <Box alignItems={"center"} overflow={"hidden"}>
      {backUrl ? (
        <BackButton onClick={() => history.push(backUrl)} />
      ) : (
        <Logo linkUrl={portfolioUrl} />
      )}
      <VBox gap={false} overflow={"hidden"}>
        <strong className={"tw-truncate"}>{companyName}</strong>
        <small className={"subtle tw-truncate"}>{formatOrgNum(orgNo)}</small>
      </VBox>
    </Box>
  );
});

export const MeetingDocumentView: FC<{ content?: string }> = observer(({ content }) => (
  <DocumentPreview
    content={[{ type: "document", documentId: "n/a", content }]}
    centered={false}
    padding={false}
  />
));

export const AttachmentsCard: FC<{
  attachments: UploadedAttachment[];
  onDownload: (fileId: string) => void;
}> = observer(({ attachments, onDownload }) => {
  const { t } = useTranslation();
  return (
    <Card title={t("attachments")}>
      {attachments.length === 0 && <div className={"subtle"}>{t("no-attachments")}</div>}
      <ul>
        {attachments.map((attachment) => (
          <li key={attachment.id} className="flex">
            <LinkButton
              tooltip={attachment.filename}
              icon={{ name: "paperclip" }}
              weight={"normal"}
              label={attachment.filename}
              onClick={() => onDownload(attachment.id)}
              truncate
            />
          </li>
        ))}
      </ul>
    </Card>
  );
});

export type DownloadAsPdfButtonProps = {
  documentType?: DocumentType;
  isDownloading?: boolean;
  onCancel?: DCCallback;
  onConfirm?: DCCallback;
  onDownload: DCCallback;
  onDownloadWithAttachments?: DCCallback;
  variant?: "link" | "secondary" | "icon";
};

export const DownloadAsPdfButton: FC<DownloadAsPdfButtonProps> = (props) => {
  const { t } = useTranslation();
  const variant = props.variant ?? "secondary";

  const isNotice =
    props.documentType === DocumentType.notice_general_meeting ||
    props.documentType === DocumentType.notice_board_meeting;

  const isMinutes =
    props.documentType === DocumentType.general_meeting ||
    props.documentType === DocumentType.board_meeting;

  return (
    <>
      {props.onConfirm && (
        <Dialog
          title={{ key: "download-non-pdf-attachment.title" }}
          onCancel={props.onCancel}
          onSubmit={props.onConfirm}
          submitLabel={{ key: "download-non-pdf-attachment.download" }}
          isSubmitting={props.isDownloading}
        >
          <div>{t("download-non-pdf-attachment.warning")}</div>
        </Dialog>
      )}
      {props.onDownloadWithAttachments ? (
        <DropdownMenu
          button={
            variant === "secondary"
              ? {
                  type: "button",
                  variant: "secondary",
                  label: { key: "download-pdf" },
                  loading: !props.onConfirm && props.isDownloading,
                }
              : variant === "icon"
                ? {
                    type: "icon",
                    icon: "download",
                    tooltip: { key: "download-pdf" },
                  }
                : {
                    type: "link",
                    label: { key: "download-pdf" },
                    loading: !props.onConfirm && props.isDownloading,
                  }
          }
          items={[
            {
              type: "item",
              icon: "download",
              label: isNotice
                ? { key: "download-as-pdf.notice-and-attachments" }
                : isMinutes
                  ? { key: "download-as-pdf.minutes-and-attachments" }
                  : { key: "download-as-pdf.document-and-attachments" },
              onSelect: props.onDownloadWithAttachments,
            },
            {
              type: "item",
              icon: "download",
              label: isNotice
                ? { key: "download-as-pdf.notice-only" }
                : isMinutes
                  ? { key: "download-as-pdf.minutes-only" }
                  : { key: "download-as-pdf.document-only" },
              onSelect: props.onDownload,
            },
          ]}
        />
      ) : variant === "secondary" ? (
        <Button
          variant={"secondary"}
          loading={props.isDownloading}
          label={{ key: "download-pdf" }}
          icon={"download"}
          onClick={props.onDownload}
        />
      ) : variant === "icon" ? (
        <IconButton
          icon={"download"}
          tooltip={{ key: "download-pdf" }}
          onClick={props.onDownload}
          loading={props.isDownloading}
        />
      ) : (
        <LinkButton
          loading={props.isDownloading}
          label={{ key: "download-pdf" }}
          icon={"download"}
          onClick={props.onDownload}
        />
      )}
    </>
  );
};

export const NoticeActionsView: FC<{
  download: DownloadAsPdfButtonProps;
  addToCalendar?: {
    title: string;
    companyName: string;
    ical: {
      [key in Language]?: string;
    };
    googleCalendar: {
      [key in Language]?: string;
    };
  };
}> = observer(({ download, addToCalendar }) => {
  const { t } = useTranslation();
  return (
    <Card>
      <VBox gap={"md"}>
        {download && <DownloadAsPdfButton {...download} />}
        {addToCalendar && (
          <div>
            <AddToCalendar
              variant={"link"}
              title={t("meeting_at", {
                meeting: addToCalendar.title,
                place: addToCalendar.companyName,
              })}
              ical={addToCalendar.ical}
              googleCalendar={addToCalendar.googleCalendar}
            />
          </div>
        )}
      </VBox>
    </Card>
  );
});

export type AddToCalendarProps = {
  variant?: "link" | "button";
  title: string;
  description?: string;
  meetingStartDate?: string;
  meetingEndDate?: string;
  meetingLocation?: string;
  ical: {
    [key in Language]?: string;
  };
  googleCalendar: {
    [key in Language]?: string;
  };
};

export const AddToCalendar: FC<AddToCalendarProps> = observer(
  ({ variant = "button", title, ical, googleCalendar }) => {
    const { t } = useTranslation();
    const locale = useLanguage();
    const defaultIcal = Object.values(ical)[0];
    const defaultGoogleCalendar = Object.values(ical)[0];
    const downloadIcal =
      Boolean(defaultIcal) &&
      (() => {
        downloadFile(
          new Blob([ical[locale] ?? defaultIcal], {
            type: "plain/text",
          }),
          title + ".ics",
        );
      });

    const openGoogleUrl =
      Boolean(defaultGoogleCalendar) &&
      (() => {
        window.open(googleCalendar[locale] ?? defaultGoogleCalendar);
      });
    return (
      <DropdownMenu
        button={
          variant === "button"
            ? {
                type: "button",
                variant: "secondary",
                label: t("add_to_calendar"),
              }
            : {
                type: "link",
                label: t("add_to_calendar"),
                weight: "normal",
                icon: "calendar",
              }
        }
        items={[
          openGoogleUrl && {
            type: "item",
            icon: "google-logo",
            label: "Google",
            onSelect: openGoogleUrl,
          },
          downloadIcal && {
            type: "item",
            icon: "microsoft-outlook-logo",
            label: "Outlook",
            onSelect: downloadIcal,
          },
          downloadIcal && {
            type: "item",
            icon: "apple-logo",
            label: "Calendar",
            onSelect: downloadIcal,
          },
        ]}
      />
    );
  },
);

export const HeaderMeetingInfo: FC<{
  title: string;
  companyName: string;
  meetingStartDate: string;
  meetingEndDate?: string;
  ical?: {
    [key in Language]?: string;
  };
  googleCalendar?: {
    [key in Language]?: string;
  };
  orgNo?: string;
  edit?: () => void;
  isCirculation?: boolean;
  deadlineForFeedback?: string;
  meetingStatus?: TagProps;
  meetingLink?: string;
  actions?: Array<ButtonProps & { key: string }>;
}> = observer(
  ({
    title,
    companyName,
    meetingStartDate,
    meetingEndDate,
    ical,
    googleCalendar,
    edit,
    isCirculation,
    deadlineForFeedback,
    meetingStatus,
    meetingLink,
    actions,
  }) => {
    const { t } = useTranslation();
    const locale = useLanguage();
    return (
      <Box
        justifyContent={isMobileView() ? undefined : "space-between"}
        maxWidth={"xl"}
        vertical={isMobileView()}
      >
        <VBox>
          <h3>
            <Box alignItems={"center"}>
              <span>{title}</span>
              {meetingStatus && <Tag {...meetingStatus} />}
            </Box>
          </h3>
          <Box gap={"md"}>
            {isCirculation ? (
              <span>{t("meeting-is-circulation")}</span>
            ) : (
              compact([
                formatDateTime(locale, meetingStartDate),
                meetingEndDate && formatTime(locale, meetingEndDate),
              ]).join("-")
            )}
            {deadlineForFeedback && (
              <span>
                {t("deadline-for-feedback", { date: formatDateTime(locale, deadlineForFeedback) })}
              </span>
            )}
            {meetingLink && (
              <a
                href={meetingLink.startsWith("http") ? meetingLink : `https://${meetingLink}`}
                target={"_blank"}
                rel="noreferrer"
                className={"undecorated ml-auto"}
                data-tooltip={meetingLink}
              >
                <Box alignItems={"center"}>
                  <Icon name={"video-camera"} color={"info"} />
                  <span>
                    {t("documents.meetingForm.electronicParticipationLink.join-video-meeting")}{" "}
                  </span>
                </Box>
              </a>
            )}
          </Box>
        </VBox>
        <Box vertical={isMobileView()}>
          {edit || actions?.length ? (
            <Box alignItems="center">
              {edit && <Button label={t("edit")} variant="secondary" onClick={edit} />}
              {actions?.map(({ key, ...props }) => (
                <Button key={key} {...props} />
              ))}
            </Box>
          ) : undefined}
          <Box alignItems="center">
            {ical && googleCalendar && !isCirculation && (
              <AddToCalendar
                title={t("meeting_at", { meeting: title, place: companyName })}
                ical={ical}
                googleCalendar={googleCalendar}
              />
            )}
          </Box>
        </Box>
      </Box>
    );
  },
);

export const MatterText = observer(
  ({
    matterText,
    isMultiLanguage,
    isInformationMatter,
  }: {
    matterText: RenderedDecisionRow[];
    isMultiLanguage: boolean;
    isInformationMatter: boolean;
  }) => {
    const { t } = useTranslation();
    const matterHasText =
      matterText.flatMap((m) => m.cells).filter((t) => isNotBlank(t)).length > 0;
    return matterHasText ? (
      <div>
        {!isInformationMatter && (
          <span className={"subtitle"}>{t("meetings.decision.to_review")}</span>
        )}
        <Grid cols={1} colsMd={isMultiLanguage ? 2 : 1}>
          {matterText.map((row, rowNumber) =>
            row.cells.map((cell, column) => (
              <GridItem key={rowNumber + "-" + column} className="content">
                {parse(removeTitleAndTD(cell), { trim: true })}
              </GridItem>
            )),
          )}
        </Grid>
      </div>
    ) : null;
  },
);

export const MatterExtraText = observer(
  ({
    texts,
    documentType,
    isMultiLanguage,
  }: {
    texts: LocalizedTextForDocument[];
    documentType: DocumentType;
    isMultiLanguage: boolean;
  }) => {
    return (
      <Grid cols={1} colsMd={isMultiLanguage ? 2 : 1}>
        {texts
          .filter((x) => x.documentType === documentType)
          .map(({ text, language }) => (
            <Markdown key={language} value={text} />
          ))}
      </Grid>
    );
  },
);

export const Attachments = observer(
  ({
    attachments,
    onDownloadAttachment,
    caption,
  }: {
    attachments: UploadedAttachment[];
    onDownloadAttachment?: (fileId: string) => void;
    caption: TMessage;
  }) => {
    const { t } = useTranslation();
    return attachments && onDownloadAttachment && attachments.length > 0 ? (
      <VBox marginTop>
        <div className={"subtitle"}>{t(caption)}</div>
        <AttachmentsPanel
          uploadedAttachments={attachments}
          onOpen={onDownloadAttachment}
          title={false}
        />
      </VBox>
    ) : null;
  },
);

// export const NewFeatureAnnouncementDialog = observer(function NewFeatureAnnouncementDialog({
//   onClose,
// }: {
//   onClose: (doNotShowAgain: boolean) => void;
// }) {
//   const { t } = useTranslation();
//   const [checked, setChecked] = useState(false);
//   const locale = useLanguage();
//   return (
//     <Dialog cancelLabel={t("close")} onCancel={() => onClose(checked)}>
//       <VBox marginLeft={"md"} alignItems={"center"} gap={"lg"} marginRight={"md"}>
//         <img src={AutumnRelease} alt={"autumnRelease"} />
//         <h2>
//           <Markdown value={t("release-announcement.title")} />
//         </h2>
//         <Box textAlign={"center"}>
//           <Markdown value={t("release-announcement.description")} />
//         </Box>
//         <Button
//           variant={"primary"}
//           label={t("release-announcement.show-release-notes")}
//           onClick={() => {
//             let url = "https://dcompany.no/docs/release-note-september-2024/";
//             if (locale === Language.EN) {
//               url = "https://dcompany.no/en/docs/release-note-september-2024/";
//             }
//             openLinkInNewWindow(url);
//           }}
//         />
//       </VBox>
//       <Box
//         justifyContent="space-between"
//         marginTop="lg"
//         alignItems="center"
//         vertical={isMobileView()}
//       >
//         <Checkbox //The upcoming release should improve the checkbox display in mobile view
//           label={t("do-not-show-message-again")}
//           checked={checked}
//           onChange={(e) => setChecked(e.target.checked)}
//         />
//       </Box>
//     </Dialog>
//   );
// });

export const AddNameAndEmailForm: FC<{
  form: { nameField: StringFieldModel; emailField: StringFieldModel };
  submit: () => void;
  cancel: () => void;
}> = observer(({ form, submit, cancel }) => {
  const { t } = useTranslation();
  return (
    <Form submitOnEnter onSubmit={submit} className={"grow"}>
      <VBox>
        <MTextInput autofocus label={t("name")} model={form.nameField} />
        <MTextInput label={t("email")} model={form.emailField} />
        <Box>
          <Button
            variant={"primary"}
            size={"sm"}
            icon={"check"}
            label={t("add")}
            onClick={submit}
          />
          <Button
            size={"sm"}
            icon={"x"}
            label={t("cancel")}
            onClick={cancel}
            variant={"secondary"}
          />
        </Box>
      </VBox>
    </Form>
  );
});

export function SendingTime({ sendingTime }: { sendingTime: string | undefined }) {
  const { t } = useTranslation();
  const locale = useLanguage();
  return sendingTime ? (
    <div className={"subtle text-xs"}>
      {t("sent-at", {
        date: formatDateTime(locale, sendingTime, { relative: true }),
      })}
    </div>
  ) : null;
}

export function LastLogin({ lastLogin }: { lastLogin: string | undefined }): ReactElement {
  const locale = useLanguage();
  const { t } = useTranslation();
  if (lastLogin === undefined) {
    return <Tag color={"neutral"} label={t("never-logged-in")} />;
  } else {
    return <div>{formatDateTime(locale, lastLogin, { relative: true })}</div>;
  }
}

export const SelectField = observer(function MRadioButtonGroup<T>({
  field,
  name,
  label,
  fullWidth,
  isLocked,
  onChange,
}: {
  field: RadioFieldModel<T>;
  name: string;
  label?: string;
  fullWidth?: boolean;
  isLocked?: boolean;
  onChange?: (option: T) => void;
}) {
  return (
    <MSelect
      fullWidth={fullWidth}
      label={label}
      name={name}
      model={field}
      disabled={isLocked}
      onSelect={onChange}
    />
  );
});
export const RadioFieldQuestion = observer(function RadioQuestion<T>({
  question,
  infoText,
  field,
  fieldName,
  row,
  isLocked,
  onChange,
}: {
  question: TMessage;
  infoText?: TMessage;
  field: RadioFieldModel<T>;
  fieldName: string;
  row?: boolean;
  isLocked?: boolean;
  onChange?: (option: T) => void;
}) {
  const { t } = useTranslation();

  return (
    <VBox>
      <div>
        {t(question)}
        &nbsp;
        <InfoButton infoText={when(infoText, t)} />
      </div>
      <MRadioButtonGroup
        disabled={isLocked}
        horizontal={row}
        name={fieldName}
        model={field}
        onChange={(_, option) => (onChange ? onChange(option) : field.setValue(option))}
      />
    </VBox>
  );
});

export type SendForSigningDialogProps = {
  form: SignersForm;
  title: TMessage;
  isSending?: boolean;
  canClose: boolean;
  canSubmit: boolean;
  information?: { icon?: IconName; message: ReactElement | string | number };
  canRemoveSigner?: (signer: SignerModel) => boolean;
  removeSigner?: (signer: SignerModel) => void;
  addSigner?: () => void;
  close: () => void;
  submit: () => void;
  submitButton?: ReactNode;
  showMessageFields?: boolean;
  showUnsupportedAttachmentWarning?: boolean;
  signingCompleteInfoMessage?: ReactElement | string;
  destinationFolderLabel?: TMessageKey;
  quickAddItems?: QuickAddSignerItems;
  hasSuccessUploadedDocuments?: boolean;
  commonSigningProperties?: CommonSigningProperties;
  preview?: FilePreviewProps;
  documentsForSigning?: DocumentsForSigning;
  companiesForSigning?: CompaniesForSigning;
  enableFullscreenMode?: boolean;
};

export const SendForSigningDialog = observer(
  ({
    form,
    title,
    isSending,
    canClose,
    canSubmit,
    information,
    canRemoveSigner,
    removeSigner,
    addSigner,
    close,
    submit,
    showMessageFields,
    showUnsupportedAttachmentWarning,
    signingCompleteInfoMessage,
    destinationFolderLabel,
    quickAddItems,
    hasSuccessUploadedDocuments,
    commonSigningProperties,
    preview,
    documentsForSigning,
    companiesForSigning,
    enableFullscreenMode,
    submitButton,
  }: SendForSigningDialogProps) => {
    const { t } = useTranslation();
    const { size, setRef } = useSize();
    return (
      <Fragment>
        {enableFullscreenMode ? (
          <Dialog fullscreen>
            <FullScreenLayout
              centerPanelContainerProps={
                isMobileView() ? undefined : { className: "tw-hidden lg:tw-flex tw-basis-1/2" }
              }
              pageHeader={
                <Box bg borderBottom padding alignItems={"center"}>
                  <IconButton
                    icon={"x"}
                    tooltip={{ key: "close" }}
                    color={"inherit"}
                    onClick={close}
                  />
                  <div>{t("send-for-signing")}</div>
                </Box>
              }
              centerPanel={
                <HiddenForMobile>
                  {preview && (
                    <VBox grow overflow={"hidden"} gap={false}>
                      <SendDocumentForSigningPreviewHeader
                        documentsForSigning={documentsForSigning}
                        companiesForSigning={companiesForSigning}
                        title={title}
                      />
                      <VBox paddingX overflow={"auto"} grow>
                        <div ref={setRef}>
                          <FilePreview {...preview} width={size.width} />
                        </div>
                      </VBox>
                    </VBox>
                  )}
                </HiddenForMobile>
              }
              rightPanelContainerProps={
                isMobileView() ? undefined : { className: "tw-basis-1/2 relative grow" }
              }
              rightPanel={
                <SendDocumentForSigningView
                  form={form}
                  showMessageFields={showMessageFields}
                  commonSigningProperties={commonSigningProperties}
                  canRemoveSigner={canRemoveSigner}
                  removeSigner={removeSigner}
                  addSigner={addSigner}
                  quickAddItems={quickAddItems}
                  signingCompleteInfoMessage={signingCompleteInfoMessage}
                  destinationFolderLabel={destinationFolderLabel}
                  information={information}
                  hasSuccessUploadedDocuments={hasSuccessUploadedDocuments}
                  showUnsupportedAttachmentWarning={showUnsupportedAttachmentWarning}
                />
              }
              footerPanel={
                <Box bg borderBottom padding border justifyContent={"end"}>
                  <Button
                    variant={"secondary"}
                    label={t("cancel")}
                    onClick={close}
                    disabled={!canClose}
                  />
                  <Button
                    variant={"primary"}
                    label={t("send")}
                    onClick={submit}
                    disabled={!canSubmit && !isSending}
                    loading={isSending}
                  />
                </Box>
              }
            />
          </Dialog>
        ) : (
          <Dialog
            title={t(title)}
            submitLabel={t("send")}
            isSubmitting={isSending}
            isSubmitDisabled={!canSubmit}
            isCancelDisabled={!canClose}
            onSubmit={submit}
            onCancel={close}
            actions={submitButton}
          >
            {form.error ? (
              <Alert type={"error"} message={t(form.error)} />
            ) : information ? (
              <Alert type={hasSuccessUploadedDocuments ? "success" : "info"} {...information} />
            ) : form.isSignerListEmpty ? (
              <Alert type={"error"} message={t("documents.empty-signer-list")} />
            ) : showUnsupportedAttachmentWarning ? (
              <Alert
                message={
                  <Markdown
                    value={t("documents.send-documents-for-signing.alert.unsupported-attachments")}
                  />
                }
                type={"warning"}
              />
            ) : undefined}
            <SendDocumentForSigningView
              form={form}
              showMessageFields={showMessageFields}
              commonSigningProperties={commonSigningProperties}
              canRemoveSigner={canRemoveSigner}
              removeSigner={removeSigner}
              addSigner={addSigner}
              quickAddItems={quickAddItems}
            />
            {showUnsupportedAttachmentWarning && (
              <Alert
                message={
                  <Markdown
                    value={t("documents.send-documents-for-signing.alert.unsupported-attachments")}
                  />
                }
                type={"warning"}
                icon={"warning"}
              />
            )}
          </Dialog>
        )}
      </Fragment>
    );
  },
);

const SendDocumentForSigningView = observer(
  ({
    form,
    showMessageFields,
    commonSigningProperties,
    canRemoveSigner,
    removeSigner,
    addSigner,
    quickAddItems,
    signingCompleteInfoMessage,
    destinationFolderLabel,
    information,
    hasSuccessUploadedDocuments,
    showUnsupportedAttachmentWarning,
  }: {
    form: SignersForm;
    showMessageFields?: boolean;
    commonSigningProperties?: CommonSigningProperties;
    canRemoveSigner?: (signer: SignerModel) => boolean;
    removeSigner?: (signer: SignerModel) => void;
    addSigner?: () => void;
    quickAddItems?: QuickAddSignerItems;
    signingCompleteInfoMessage?: ReactElement | string;
    destinationFolderLabel?: TMessageKey;
    information?: { icon?: IconName; message: ReactElement | string | number };
    hasSuccessUploadedDocuments?: boolean;
    showUnsupportedAttachmentWarning?: boolean;
  }) => {
    const { t } = useTranslation();
    return (
      <VBox container marginTop={"lg"}>
        {form.error ? (
          <Alert type={"error"} message={form.error} />
        ) : information ? (
          <Alert type={hasSuccessUploadedDocuments ? "success" : "info"} {...information} />
        ) : form.isSignerListEmpty ? (
          <Alert type={"error"} message={{ key: "documents.empty-signer-list" }} />
        ) : showUnsupportedAttachmentWarning ? (
          <Alert
            message={
              <Markdown
                value={t("documents.send-documents-for-signing.alert.unsupported-attachments")}
              />
            }
            type={"warning"}
          />
        ) : undefined}
        {commonSigningProperties && (
          <>
            {showMessageFields && (
              <EmailSettings form={form} commonSigningProperties={commonSigningProperties} />
            )}
            <RecipientsComponent
              form={form}
              commonSigningProperties={commonSigningProperties}
              canRemoveSigner={canRemoveSigner}
              removeSigner={removeSigner}
            />
          </>
        )}
        <AddPersonAndOtherItems addSigner={addSigner} quickAddItems={quickAddItems} />
        <RecipientsForSealedCopy
          form={form}
          signingCompleteInfoMessage={signingCompleteInfoMessage}
          destinationFolderLabel={destinationFolderLabel}
        />
      </VBox>
    );
  },
);

const EmailSettings = observer(
  ({
    form,
    commonSigningProperties,
  }: {
    form: SignersForm;
    commonSigningProperties: CommonSigningProperties;
  }) => {
    const { t } = useTranslation();
    const en = useTranslation(Language.EN);
    const no = useTranslation(Language.NO);
    useEffect(
      () =>
        form.init(
          commonSigningProperties.languageFieldModel.value === Language.NO
            ? no.t(form.emailTemplate)
            : en.t(form.emailTemplate),
        ),
      [form, en, no, commonSigningProperties.languageFieldModel.value],
    );
    return (
      <VBox gap={"md"}>
        <strong>{t("email")}</strong>
        <Box gap={"lg"} wrap={isMobileView()}>
          <div>{t("customize-message")}</div>
          <MRadioButtonGroup horizontal model={commonSigningProperties.languageFieldModel} />
        </Box>
        <LocalizedTextEditor model={form.emailBody} />
        {form.showBatchEmailsSettings && (
          <>
            <Box gap={"lg"} wrap={isMobileView()}>
              <div>{t("signing.batch-emails.label")}</div>
              <MRadioButtonGroup model={form.batchEmails} horizontal />
            </Box>
            {!form.dismissedBatchEmailsInfo && (
              <Alert
                type="info"
                message={{ key: "signing.batch-emails.info" }}
                onClose={form.dismissBatchEmailsInfo}
              />
            )}
          </>
        )}
      </VBox>
    );
  },
);
const RecipientsComponent = observer(
  ({
    form,
    commonSigningProperties,
    canRemoveSigner,
    removeSigner,
  }: {
    form: SignersForm;
    commonSigningProperties: CommonSigningProperties;
    canRemoveSigner?: (signer: SignerModel) => boolean;
    removeSigner?: (signer: SignerModel) => void;
  }) => {
    const { t } = useTranslation();
    return (
      <VBox gap={"lg"} marginTop={"md"}>
        <Box
          vertical={isMobileView()}
          justifyContent={"space-between"}
          alignItems={isMobileView() ? "start" : "center"}
        >
          <strong>{t("archive.send-for-signing.send-to")}</strong>
          <Box
            vertical={isMobileView()}
            gap={isMobileView() ? "md" : "xl"}
            alignItems={isMobileView() ? "start" : "center"}
            marginRight={"sm"}
          >
            {t("signatures.send-documents-for-signing.signing-method")}
            <Box>
              <MSelect model={commonSigningProperties.signingMethodFieldModel} fullWidth />
            </Box>
          </Box>
        </Box>
        {form.groupedItems.map(([companyName, items]) => (
          <VBox key={companyName} gap={"md"}>
            {form.showGroupHeading(companyName) && <Box fontWeight={"bold"}>{companyName}</Box>}
            <Table variant={"IN_CARD"} inline={!isMobileView()}>
              <thead>
                <tr>
                  <th align={"left"} style={{ minWidth: "150px" }}>
                    {t("name")}
                  </th>
                  <th align={"left"} style={{ minWidth: "150px" }}>
                    {t("email")}
                  </th>
                  <th align={"left"}>
                    {t("signatures.send-documents-for-signing.signing-method.custom")}
                  </th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((signer) => (
                  <tr key={signer.key}>
                    <td>
                      <Box justifyContent={"space-between"}>
                        <MTextInput
                          placeholder={t("name")}
                          model={signer.nameField}
                          addon={
                            signer.signerType === SignerType.SUBSCRIPTION ? (
                              <Box marginRight={"sm"} alignItems={"center"}>
                                <Icon name={"certificate"} />
                              </Box>
                            ) : undefined
                          }
                        />
                      </Box>
                    </td>
                    <td>
                      <MTextInput placeholder={t("email")} model={signer.emailField} />
                    </td>
                    <td>
                      <Box>
                        <SelectField
                          name={"signingMethod"}
                          field={signer.methodField}
                          onChange={(option) => {
                            signer.setSigningMethod(option);
                            commonSigningProperties.changeCommonSigningMethod();
                          }}
                          fullWidth
                        />
                        {canRemoveSigner?.(signer) && (
                          <IconButton
                            tooltip={t("remove")}
                            onClick={(event) => {
                              event.preventDefault();
                              removeSigner?.(signer);
                              commonSigningProperties.changeCommonSigningMethod();
                            }}
                            icon={"trash"}
                            color={"error"}
                          />
                        )}
                      </Box>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </VBox>
        ))}
      </VBox>
    );
  },
);

const RecipientsForSealedCopy = observer(
  ({
    form,
    signingCompleteInfoMessage,
    destinationFolderLabel,
  }: {
    form: SignersForm;
    signingCompleteInfoMessage?: ReactElement | string;
    destinationFolderLabel?: TMessageKey;
  }) => {
    const { t } = useTranslation();
    return (
      <VBox marginTop={"lg"} marginBottom={"lg"}>
        <strong>{t("signatures.send-documents-for-signing.sealed-copy")}</strong>
        <Grid
          marginBottom={form.isSendSealedCopyToOthersSelected ? "md" : "sm"}
          cols={3}
          gap={"lg"}
        >
          <div> {t("send-for-signing.send-to")}</div>
          <GridItem colSpan={2}>
            <MCheckboxGroup horizontal model={form.sendSealedCopyTo} name={"signingCompleteCopy"} />
          </GridItem>
          {form.isSendSealedCopyToOthersSelected && (
            <GridItem colSpan={3}>
              <SendSealedCopyToOtherRecipients
                model={form.sendSealedCopyToOthers}
                enableRemoveRecipient={form.enableRemoveOtherRecipients}
                removeRecipient={form.removeOtherRecipient}
                addRecipient={form.addOtherRecipient}
              />
            </GridItem>
          )}
          {destinationFolderLabel && (
            <>
              <div> {t("send-for-signing.save-to-archive")}</div>
              <GridItem colSpan={2}>
                <Box alignItems={"center"}>
                  <Icon name={"folder"} />
                  <span>{t(destinationFolderLabel)}</span>
                  <InfoButton infoText={signingCompleteInfoMessage} />
                </Box>
              </GridItem>
            </>
          )}
          {form.sharepointContext && (
            <>
              <Box alignItems={"center"}> {t("send-for-signing.save-to-sharepoint")}</Box>
              <GridItem colSpan={2}>
                <Box alignItems={"center"}>
                  {form.isSharepointEnabled ? (
                    <ConfigureSharepointDestination form={form} />
                  ) : (
                    <>
                      <LinkButton
                        label={t("dbot.select-sharepoint-destination")}
                        icon={"folder-open"}
                        weight={"normal"}
                        disabled
                      />
                      <InfoButton
                        infoText={{
                          key: "send-for-signing.save-to-sharepoint.contact-sales",
                          params: {
                            company: encodeURI(form.sharepointContext.company.name),
                            orgNo: form.sharepointContext.company.organizationnumber,
                            email: form.sharepointContext.user.email ?? "",
                            customerName:
                              encodeURI(formatUserName(form.sharepointContext.user)) ?? "",
                          },
                        }}
                      />
                    </>
                  )}
                </Box>
              </GridItem>
            </>
          )}
        </Grid>
      </VBox>
    );
  },
);

const SendSealedCopyToOtherRecipients = observer(
  ({
    model,
    enableRemoveRecipient,
    removeRecipient,
    addRecipient,
  }: {
    model: SendSealedCopyToOtherModel[];
    enableRemoveRecipient: boolean;
    removeRecipient: (recipient: SendSealedCopyToOtherModel) => void;
    addRecipient: () => void;
  }) => {
    const { t } = useTranslation();
    return (
      <VBox gap={"md"}>
        <Table variant={"IN_CARD"} inline={!isMobileView()}>
          <thead>
            <tr>
              <th align={"left"} style={{ minWidth: "150px" }}>
                {t("name")}
              </th>
              <th align={"left"} style={{ minWidth: "150px" }}>
                {t("email")}
              </th>
            </tr>
          </thead>
          <tbody>
            {model.map((recipient) => (
              <tr key={recipient.id}>
                <td className={"align-top"}>
                  <MTextInput placeholder={t("name")} model={recipient.name} />
                </td>
                <td className={"align-top"}>
                  <MTextInput placeholder={t("email")} model={recipient.email} />
                </td>
                <td className={"align-top"}>
                  {enableRemoveRecipient && (
                    <IconButton
                      tooltip={t("remove")}
                      onClick={(event) => {
                        event.preventDefault();
                        removeRecipient?.(recipient);
                      }}
                      icon={"trash"}
                      color={"error"}
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <Box>
          <LinkButton icon={"plus"} label={{ key: "addOtherRecipients" }} onClick={addRecipient} />
        </Box>
      </VBox>
    );
  },
);

const AddPersonAndOtherItems = observer(
  ({
    addSigner,
    quickAddItems,
  }: {
    addSigner?: () => void;
    quickAddItems?: QuickAddSignerItems;
  }) => {
    const { t } = useTranslation();

    return (
      <Fragment>
        {(Boolean(addSigner) || (quickAddItems && quickAddItems.length > 0)) && (
          <VBox gap={"lg"}>
            {Boolean(addSigner) && (
              <Box>
                <LinkButton
                  label={t("addPerson")}
                  icon={"plus"}
                  onClick={(event) => {
                    event.preventDefault();
                    addSigner?.();
                  }}
                />
              </Box>
            )}
            {quickAddItems && quickAddItems.length > 0 && (
              <VBox gap={"md"}>
                {quickAddItems.map(
                  (row) =>
                    row.items.length > 0 && (
                      <Box wrap key={row.rowId}>
                        {row.items.map((q) => (
                          <ToggleButton
                            testId={q.id.toString()}
                            key={q.id}
                            icon={"plus"}
                            variant={"small"}
                            label={t(q.label)}
                            onClick={preventDefault(q.action)}
                          />
                        ))}
                      </Box>
                    ),
                )}
              </VBox>
            )}
          </VBox>
        )}
      </Fragment>
    );
  },
);

const SendDocumentForSigningPreviewHeader = observer(
  ({
    documentsForSigning,
    companiesForSigning,
    title,
  }: {
    documentsForSigning?: DocumentsForSigning;
    companiesForSigning?: CompaniesForSigning;
    title: TMessage;
  }) => {
    const { t } = useTranslation();
    return (
      <Grid bg borderBottom padding alignItems={"center"} gap={"md"}>
        {companiesForSigning && companiesForSigning.companies.length > 1 && (
          <GridItem xs={6}>
            <Box grow>
              <Button
                variant={"basic"}
                icon={"caret-up"}
                tooltip={t("previous")}
                onClick={companiesForSigning.previousCompany}
                disabled={!companiesForSigning.previousCompany}
              />
              <Button
                variant={"basic"}
                icon={"caret-down"}
                tooltip={t("next")}
                onClick={companiesForSigning.nextCompany}
                disabled={!companiesForSigning.nextCompany}
              />
              <Select
                icon={"buildings"}
                value={companiesForSigning.selectedCompany}
                items={companiesForSigning.companies}
                onSelect={companiesForSigning.selectCompany}
                idFn={(company) => company.organisationNumber}
                labelFn={(company) => company.companyName}
              />
            </Box>
          </GridItem>
        )}
        {documentsForSigning ? (
          <GridItem xs={companiesForSigning && companiesForSigning.companies.length > 1 ? 6 : 12}>
            {documentsForSigning.documents.length > 1 ? (
              <Box grow>
                <Select
                  icon={"file-text"}
                  value={documentsForSigning.selectedDocument}
                  items={documentsForSigning.documents}
                  onSelect={documentsForSigning.selectDocument}
                  idFn={(document) => document.id + document?.reference}
                  labelFn={(document) => document.name}
                />
                <Button
                  variant={"basic"}
                  icon={"caret-left"}
                  tooltip={t("previous")}
                  onClick={documentsForSigning.previousDocument}
                  disabled={!documentsForSigning.previousDocument}
                />
                <Button
                  variant={"basic"}
                  icon={"caret-right"}
                  tooltip={t("next")}
                  onClick={documentsForSigning.nextDocument}
                  disabled={!documentsForSigning.nextDocument}
                />
              </Box>
            ) : (
              <Box>
                <Icon name={"file-text"} />
                <div
                  className={"tw-truncate"}
                  data-tooltip={
                    companiesForSigning && companiesForSigning.companies.length > 1
                      ? documentsForSigning.selectedDocument?.name
                      : undefined
                  }
                >
                  {documentsForSigning.selectedDocument?.name}
                </div>
              </Box>
            )}
          </GridItem>
        ) : (
          <GridItem xs={12}>
            <Box>
              <Icon name={"file-text"} />
              <div className={"tw-truncate"}>{t(title)}</div>
            </Box>
          </GridItem>
        )}
      </Grid>
    );
  },
);

export const ConfirmDeleteSigningRequestDialog = observer(
  ({
    isSubmitting,
    onConfirm,
    onCancel,
  }: {
    isSubmitting?: boolean;
    onConfirm: () => void;
    onCancel: () => void;
  }) => {
    const { t } = useTranslation();
    return (
      <Dialog
        maxWidth={"sm"}
        title={t("archive.delete-signing-request.title")}
        onCancel={onCancel}
        onSubmit={onConfirm}
        submitLabel={getActionLabel("delete-signing-request")}
        isSubmitting={isSubmitting}
        submitColor={"error"}
      >
        <VBox gap={"lg"}>
          <p>{t("archive.delete-signing-request.description")}</p>
        </VBox>
      </Dialog>
    );
  },
);

export const CopyToClipboard = ({ text }: { text: string }) => (
  <IconButton
    icon={"copy"}
    tooltip={{ key: "general.copy-to-clipboard" }}
    onClick={[copyToClipboard(text)]}
  />
);
